import React, { useState } from 'react';
import { useEffect } from 'react';

import { createStyles, RadioGroup, Theme, withStyles, InputBase, makeStyles, MenuItem } from '@material-ui/core';
import { RadioProps } from '@material-ui/core/Radio';
import { ColorPalette } from 'theme/colors';

import BottomSheet from 'components/BottomSheet';
import Button from 'components/Buttons/Button';
import DataPicker, { DataPickerModel } from 'components/TimePicker/components/DataPicker';
import useStoreTranslation from 'hooks/useStoreTranslation';

import { FlightTimeType, FLIGHT_PERIODS } from './constants';
import {
  Header,
  Title,
  Close,
  FormControl,
  FormControlLabel,
  RadioButton as StyledRadio,
  FormItem,
  FormItemContainer,
  Select,
} from './styles';

export interface Props {
  show: boolean;
  onClose: () => void;
  onConfirm: (value?: string) => void;
  selected?: string;
  title: string;
  button: string;
}

const SimpleInput = withStyles((theme: Theme) =>
  createStyles({
    root: {
      'label + &': {
        marginTop: theme.spacing(3),
      },
    },
    input: {
      borderRadius: 4,
      position: 'relative',
      backgroundColor: theme.palette.background.paper,
      border: '1px solid #ced4da',
      fontSize: 14,
      padding: '8px 26px 8px 12px',
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      '&:focus': {
        borderRadius: 4,
        borderColor: '#80bdff',
        boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
      },
    },
  }),
)(InputBase);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    margin: {
      margin: theme.spacing(1),
    },
  }),
);

const FlightTimePicker = ({ show, onClose, onConfirm, selected, title, button }: Props) => {
  const classes = useStyles();
  const [value, setValue] = useState('10:00 AM');
  const [opt, setOpt] = useState('undefined');
  const [period, setPeriod] = useState<FlightTimeType | string>(FLIGHT_PERIODS[0].value);
  const [initial, setInitial] = useState({ opt: opt, value: value, period: period });

  useEffect(() => {
    if (selected) {
      if (selected.indexOf('AM') > -1 || selected.indexOf('PM') > -1) {
        setInitial({ opt: 'defined', value: selected, period: `${FLIGHT_PERIODS[0].value}` });
      } else {
        setInitial({ opt: 'predefined', value: '10:00 AM', period: selected });
      }
    } else {
      setInitial({ opt: 'undefined', value: '10:00 AM', period: `${FLIGHT_PERIODS[0].value}` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    if (show) {
      if (selected) {
        if (selected.indexOf('AM') > -1 || selected.indexOf('PM') > -1) {
          setValue(selected);
          setOpt('defined');
        } else {
          setOpt('predefined');
          setPeriod(selected);
        }
      } else {
        setOpt('undefined');
      }
    }
  }, [selected, show]);

  const { t } = useStoreTranslation();
  const populateTime = (from: number, to: number, interval: number): string[] => {
    const arr: string[] = [];
    for (let i = from; i <= to; i++) {
      for (let j = 0; j < 60; j += interval) {
        const hour = i;
        const minutes = j;
        let text = hour < 10 ? `0${hour}` : `${hour}`;
        text += ':';
        text += minutes < 10 ? `0${minutes}` : `${minutes}`;
        arr.push(text);
      }
    }
    return arr;
  };

  const arr = populateTime(1, 12, 30);
  const arrPeriod = ['AM', 'PM'];
  const data = [
    { list: arr, textAlign: 'right' },
    {
      list: arrPeriod,
      textAlign: 'left',
    },
  ] as DataPickerModel[];

  const onChange = (value: string): void => {
    setValue(value);
  };

  const handleConfirm = (): void => {
    if (opt === 'defined') return onConfirm(value);
    if (opt === 'undefined') return onConfirm(undefined);
    return onConfirm(period);
  };

  const RadioButton = withStyles({
    root: {
      '&$checked': {
        color: `${ColorPalette.HEX.OriolesOrange} !important`,
      },
    },
    checked: {
      color: `${ColorPalette.HEX.OriolesOrange} !important`,
    },
  })((props: RadioProps) => <StyledRadio color="default" {...props} />);

  return data ? (
    <>
      <BottomSheet show={show} onClose={onClose} noHeader>
        <>
          <Header>
            <Title>{title}</Title>
            <Close src="/images/close.svg" onClick={onClose} />
          </Header>
          <FormControl className={classes.margin}>
            <RadioGroup
              defaultValue={initial.opt}
              aria-label="time"
              name="time-radios"
              onChange={(ev, value) => setOpt(value)}
            >
              <FormItem>
                <FormControlLabel
                  value="undefined"
                  control={<RadioButton />}
                  label={t('flightsSearch.tripTime.anytime')}
                />
              </FormItem>
              <FormItem>
                <FormControlLabel
                  value="predefined"
                  control={<RadioButton />}
                  label={t('flightsSearch.tripTime.predefined')}
                />
                {opt === 'predefined' ? (
                  <FormItemContainer>
                    <Select
                      defaultValue={initial.period}
                      input={<SimpleInput />}
                      onChange={(ev, item) => setPeriod(item ? item['props']['value'] : '')}
                    >
                      {FLIGHT_PERIODS.map((p, k) => (
                        <MenuItem key={k} value={p.value}>
                          {t(`flightsSearch.tripTime.${p.value}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormItemContainer>
                ) : null}
              </FormItem>
              <FormItem>
                <FormControlLabel
                  value="defined"
                  control={<RadioButton />}
                  label={t('flightsSearch.tripTime.selectTime')}
                />
              </FormItem>
            </RadioGroup>
          </FormControl>
          {opt && opt === 'defined' && <DataPicker data={data} initialValue={initial.value} change={onChange} />}
          <Button
            onClick={() => {
              handleConfirm();
            }}
          >
            {button}
          </Button>
        </>
      </BottomSheet>
    </>
  ) : (
    <div>loading</div>
  );
};
export default FlightTimePicker;
